import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
  PageHeader,
  DetailCard,
  StatusChip,
  InvoiceImportLineItemRow,
  BarcodeScannerModal,
  InvoiceImportPageImage,
  ConfirmDialog,
} from '../components';
import {
  useInvoiceImport,
  useUpdateInvoiceImport,
  useConfirmInvoiceImport,
  useRetryInvoiceImport,
  useDeleteInvoiceImport,
} from '../hooks/useInvoiceImports';
import { useVendors } from '../hooks/useVendors';
import { useWarehouses } from '../hooks/useWarehouses';
import { useCategories } from '../hooks/useCategories';
import { useBusinessUuid } from '../useBusinessUuid';
import { matchProducts, getMenuPricesByInventoryUuids } from '../api/products';
import { generateUniqueBarcodes } from '../utils/barcode';
import { nextLineItemId } from '../utils/lineItems';
import { LINE_ITEMS_CONTAINER_SX } from '../utils/lineItemCounter';
import {
  colors,
  formatCurrency,
  SHOW_CURRENCY_FIELD,
  SHOW_WAREHOUSE_FIELDS,
} from '../constants';

const toDatePart = (iso) => (iso ? iso.split('T')[0] : '');

function mapToEditItems(invoice) {
  if (!invoice.extraction) return [];
  return invoice.extraction.items.map((item) => {
    const name = item.name ?? '';
    const sku = item.sku ?? '';
    const barcodes = item.barcodes ?? (item.barcode ? [item.barcode] : []);
    return {
      // Stable identity, used as the React key and to address the line in state.
      id: nextLineItemId(),
      name,
      search: '',
      sku,
      barcodes,
      productType: item.productType ?? 'product',
      quantity: item.quantity != null ? String(item.quantity) : '',
      unitCost: item.unitCost != null ? String(item.unitCost) : '',
      // MMS menu/selling price entered during review (not extracted by the AI).
      sellingPrice: item.sellingPrice != null ? String(item.sellingPrice) : '',
      // Matching is resolved client-side (see the matchProducts effect):
      //  - strong match (SKU/barcode) -> auto-linked (selectedItem/matchedProductUuid)
      //  - weak match (same name)     -> suggestedItem (tap to link)
      matchedProductUuid: null,
      selectedItem: null,
      suggestedItem: null,
      categoryId: item.categoryId ?? null,
      categoryName: item.categoryName ?? null,
      // Cost-handling choice for matched products; restored from a prior save.
      costMode: item.costMode ?? 'update',
      // Original extracted values, restored when the line is unlinked so we
      // don't keep the linked product's sku/name/barcodes.
      original: { name, sku, barcodes },
    };
  });
}

export default function InvoiceImportReview() {
  const { uuid } = useParams();
  const history = useHistory();
  const businessUuid = useBusinessUuid();

  const { data: invoice, isLoading } = useInvoiceImport(uuid);
  const { data: vendors } = useVendors({ active: true });
  const { data: warehouses = [] } = useWarehouses();
  const { data: categories = [] } = useCategories(businessUuid);

  const updateImport = useUpdateInvoiceImport();
  const confirmImport = useConfirmInvoiceImport();
  const retryImport = useRetryInvoiceImport();
  const deleteImport = useDeleteInvoiceImport();

  const [initialized, setInitialized] = useState(false);
  const [productMatchTried, setProductMatchTried] = useState(false);
  const [barcodesGenerated, setBarcodesGenerated] = useState(false);
  const [vendorUuid, setVendorUuid] = useState('');
  const [warehouseUuid, setWarehouseUuid] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [currency, setCurrency] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [items, setItems] = useState([]);
  // Ids of the lines whose menu category was missing on the last save attempt,
  // so the message can be shown under the field itself.
  const [missingCategoryIds, setMissingCategoryIds] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanTargetId, setScanTargetId] = useState(null);

  // Lets the one-off barcode generation below read the current lines without
  // listing `items` as a dependency, which would re-run its scan of the whole
  // list on every keystroke. Declared before that effect so it is already in
  // sync when the effect runs.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (invoice && invoice.status === 'review' && !initialized) {
      const e = invoice.extraction;
      setVendorUuid(e?.vendorUuid ?? '');
      setInvoiceNumber(e?.invoiceNumber ?? '');
      setInvoiceDate(toDatePart(e?.invoiceDate));
      setCurrency(e?.currency ?? '');
      setTaxAmount(e?.taxAmount != null ? String(e.taxAmount) : '');
      setShippingCost(e?.shippingCost != null ? String(e.shippingCost) : '');
      const mappedItems = mapToEditItems(invoice);
      setItems(mappedItems);
      setInitialized(true);
    }
  }, [invoice, initialized]);

  // Resolve extracted line items against existing products client-side (same
  // approach as the original inventory app): strong match (SKU/barcode) links
  // automatically; a same-name match becomes a suggestion needing Confirm.
  useEffect(() => {
    if (!initialized || productMatchTried) return;
    const extracted = invoice?.extraction;
    if (!extracted) return;
    const skus = extracted.items.map((i) => i.sku).filter(Boolean);
    const barcodes = extracted.items
      .flatMap((i) => i.barcodes ?? (i.barcode ? [i.barcode] : []))
      .filter(Boolean);
    const names = extracted.items.map((i) => i.name).filter(Boolean);
    if (skus.length === 0 && barcodes.length === 0 && names.length === 0) {
      setProductMatchTried(true);
      return;
    }
    let active = true;
    matchProducts(skus, barcodes, names, businessUuid)
      .then((found) => {
        if (!active) return;
        const list = Array.isArray(found) ? found : [];
        const bySku = new Map();
        const byBarcode = new Map();
        const byName = new Map();
        list.forEach((f) => {
          if (f.sku) bySku.set(String(f.sku).trim().toLowerCase(), f);
          (f.barcodes || []).forEach((b) =>
            byBarcode.set(String(b).trim().toLowerCase(), f),
          );
          if (f.name) byName.set(String(f.name).trim().toLowerCase(), f);
        });
        setItems((prev) =>
          prev.map((it) => {
            if (it.selectedItem || it.suggestedItem) return it;
            const strong =
              (it.sku && bySku.get(String(it.sku).trim().toLowerCase())) ||
              (it.barcodes || [])
                .map((b) => byBarcode.get(String(b).trim().toLowerCase()))
                .find(Boolean) ||
              null;
            if (strong) {
              return {
                ...it,
                search: strong.sku ?? it.search,
                sku: strong.sku ?? it.sku,
                name: strong.name ?? it.name,
                barcodes: strong.barcodes?.length ? strong.barcodes : it.barcodes,
                productType: strong.type ?? it.productType,
                selectedItem: strong,
                matchedProductUuid: strong.uuid,
                // Existing product: display its category (read-only), resolved
                // from the categories list via this id.
                categoryId: strong.categoryId ?? null,
                categoryName: null,
              };
            }
            const named = it.name
              ? byName.get(String(it.name).trim().toLowerCase())
              : null;
            return named ? { ...it, suggestedItem: named } : it;
          }),
        );
        // Prefill current menu prices for the strongly-matched products.
        prefillSellingPrices(list.map((f) => f.uuid));
      })
      .finally(() => {
        if (active) setProductMatchTried(true);
      });
    return () => {
      active = false;
    };
  }, [initialized, productMatchTried, invoice, businessUuid]);

  // Lines the matcher found nothing for will be created as new items, so an
  // empty barcode gets a generated one. Runs once, right after matching, so the
  // code is visible in the Barcodes field and can still be edited or removed.
  useEffect(() => {
    if (!productMatchTried || barcodesGenerated) return undefined;
    const needing = itemsRef.current.reduce((acc, it) => {
      const unmatched = !it.selectedItem && !it.suggestedItem;
      if (unmatched && !(it.barcodes?.length > 0)) acc.push(it.id);
      return acc;
    }, []);
    if (needing.length === 0) {
      setBarcodesGenerated(true);
      return undefined;
    }
    let active = true;
    generateUniqueBarcodes(needing.length, businessUuid)
      .then((codes) => {
        if (!active) return;
        setItems((prev) =>
          prev.map((it) => {
            const position = needing.indexOf(it.id);
            if (position === -1) return it;
            const barcodes = [codes[position]];
            // Also treated as the line's original value so unlinking a product
            // later restores the generated code rather than an empty field.
            return { ...it, barcodes, original: { ...it.original, barcodes } };
          }),
        );
        setBarcodesGenerated(true);
      })
      .catch((e) => {
        if (!active) return;
        setError(
          e?.response?.data?.message ||
            'Failed to generate barcodes for the new items.',
        );
        setBarcodesGenerated(true);
      });
    return () => {
      active = false;
    };
  }, [productMatchTried, barcodesGenerated, businessUuid]);

  useEffect(() => {
    if (invoice?.status === 'confirmed' && invoice.invoiceUuid) {
      history.replace(`/inventory-management/purchase-invoices/${invoice.invoiceUuid}`);
    }
  }, [invoice, history]);

  const vendorOptions = useMemo(
    () => (vendors || []).map((v) => ({ value: v.uuid, label: v.name })),
    [vendors]
  );
  const warehouseOptions = useMemo(
    () => warehouses.filter((w) => w.isActive).map((w) => ({ value: w.uuid, label: w.name })),
    [warehouses]
  );
  // Resolving a line's category by scanning the list would cost one pass per
  // line on every render of the form.
  const categoryById = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitCost) || 0),
        0
      ),
    [items]
  );
  const total = subtotal + (parseFloat(taxAmount) || 0) + (parseFloat(shippingCost) || 0);

  const suggestionCount = useMemo(
    () => items.filter((it) => !it.selectedItem && it.suggestedItem).length,
    [items],
  );

  // Prefill each matched line's Price with the product's CURRENT menu selling
  // price from MMS (fetched via api01). Best-effort: only fills lines that are
  // still blank, and never blocks review if the lookup fails.
  const prefillSellingPrices = useCallback(async (uuids) => {
    const list = (uuids || []).filter(Boolean);
    if (!list.length) return;
    try {
      const prices = await getMenuPricesByInventoryUuids(list);
      setItems((prev) =>
        prev.map((it) => {
          const uuid = it.matchedProductUuid;
          if (
            uuid &&
            prices[uuid] != null &&
            (it.sellingPrice === '' || it.sellingPrice == null)
          ) {
            return { ...it, sellingPrice: String(prices[uuid]) };
          }
          return it;
        }),
      );
    } catch (e) {
      console.error('Failed to load current menu price(s)', e);
    }
  }, []);

  // `updates` may be an object or, when the patch depends on the line's current
  // values, a function of that line. The function form lets the rows keep stable
  // handlers, which is what keeps them from re-rendering on unrelated edits.
  const updateItem = useCallback(
    (id, updates) =>
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, ...(typeof updates === 'function' ? updates(it) : updates) }
            : it,
        ),
      ),
    [],
  );
  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    setMissingCategoryIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const addItem = useCallback(
    () =>
      setItems((prev) => [
        ...prev,
        {
          id: nextLineItemId(),
          name: '', search: '', sku: '', barcodes: [], productType: 'product', quantity: '',
          unitCost: '', sellingPrice: '', matchedProductUuid: null, selectedItem: null,
          suggestedItem: null,
          categoryId: null, categoryName: null, costMode: 'update',
          original: { name: '', sku: '', barcodes: [] },
        },
      ]),
    [],
  );

  const handleItemSelected = useCallback(
    (id, found) => {
      updateItem(id, {
        search: found.sku,
        sku: found.sku,
        name: found.name,
        barcodes: found.barcodes || [],
        productType: found.type,
        matchedProductUuid: found.uuid,
        selectedItem: found,
        suggestedItem: null,
        // Existing product: only the category id comes from inventory; the
        // display name is resolved from the categories list. Read-only for
        // existing items.
        categoryId: found.categoryId ?? null,
        categoryName: null,
        // Changing the selected product must reset the menu price: clear the
        // previous product's price so it is refilled below with THIS product's
        // current MMS price (or left blank if that product has no menu price).
        sellingPrice: '',
      });
      // Prefill the menu selling price from MMS for this existing product.
      if (found.uuid) prefillSellingPrices([found.uuid]);
    },
    [updateItem, prefillSellingPrices],
  );

  const handleCategoryChange = useCallback(
    (id, category) => {
      updateItem(id, {
        categoryId: category?.id ?? null,
        categoryName: category?.name ?? null,
      });
      if (category?.id) {
        setMissingCategoryIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [updateItem],
  );

  const handleBarcodeScanRequest = useCallback((id) => {
    setScanTargetId(id);
    setScannerOpen(true);
  }, []);

  // Link every line that has a pending suggestion to its matched product
  // (mirrors the original inventory app).
  const linkAllSuggestions = () => {
    const linkedUuids = items
      .filter((it) => !it.selectedItem && it.suggestedItem)
      .map((it) => it.suggestedItem.uuid);
    setItems((prev) =>
      prev.map((it) => {
        if (it.selectedItem || !it.suggestedItem) return it;
        const found = it.suggestedItem;
        return {
          ...it,
          search: found.sku,
          sku: found.sku,
          name: found.name,
          barcodes: found.barcodes || [],
          productType: found.type,
          matchedProductUuid: found.uuid,
          selectedItem: found,
          suggestedItem: null,
          // Existing product: display its category (read-only), resolved from
          // the categories list via this id.
          categoryId: found.categoryId ?? null,
          categoryName: null,
        };
      }),
    );
    // Prefill current menu prices for the newly-linked products.
    prefillSellingPrices(linkedUuids);
  };

  const handleBarcodeFound = (found) => {
    if (scanTargetId && items.some((it) => it.id === scanTargetId)) {
      handleItemSelected(scanTargetId, found);
    }
  };

  const handleBarcodeNotFound = (code) => {
    if (scanTargetId && items.some((it) => it.id === scanTargetId)) {
      updateItem(scanTargetId, (line) => ({
        search: code,
        selectedItem: null,
        matchedProductUuid: null,
        barcodes: line.barcodes.includes(code)
          ? line.barcodes
          : [...line.barcodes, code],
      }));
      setError(`No product found for barcode "${code}". Added as a new barcode.`);
    }
  };

  const buildPayload = () => ({
    businessUuid,
    vendorUuid: vendorUuid || null,
    invoiceNumber: invoiceNumber.trim() || null,
    invoiceDate: invoiceDate || null,
    currency: currency.trim() || null,
    taxAmount: parseFloat(taxAmount) || 0,
    shippingCost: parseFloat(shippingCost) || 0,
    totalAmount: total,
    items: items.map((it) => {
      const quantity = parseFloat(it.quantity) || 0;
      const unitCost = parseFloat(it.unitCost) || 0;
      const sellingPrice =
        it.sellingPrice !== '' &&
        it.sellingPrice != null &&
        Number.isFinite(parseFloat(it.sellingPrice))
          ? parseFloat(it.sellingPrice)
          : null;
      return {
        name: it.name,
        sku: it.sku || null,
        barcodes: it.barcodes?.length ? it.barcodes : null,
        productType: it.productType,
        quantity,
        unitCost,
        sellingPrice,
        lineTotal: quantity * unitCost,
        matchedProductUuid: it.matchedProductUuid,
        categoryId: it.matchedProductUuid ? null : it.categoryId || null,
        categoryName: it.matchedProductUuid ? null : it.categoryName || null,
        // Cost handling only applies to existing (matched) products.
        costMode: it.matchedProductUuid ? it.costMode || 'update' : null,
      };
    }),
  });

  const lineNeedsCategory = (it) =>
    !it.matchedProductUuid && it.productType === 'product' && !it.categoryId;

  const validate = () => {
    setMissingCategoryIds(
      new Set(items.filter(lineNeedsCategory).map((it) => it.id)),
    );
    if (items.length === 0) return 'Add at least one line item.';
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (!it.sku.trim() && !it.name.trim()) return `Line ${i + 1}: SKU or name is required.`;
      const qty = parseFloat(it.quantity);
      if (!Number.isFinite(qty) || qty <= 0) return `Line ${i + 1}: quantity must be greater than zero.`;
      const cost = parseFloat(it.unitCost);
      if (!Number.isFinite(cost) || cost < 0) return `Line ${i + 1}: unit cost is invalid.`;
      if (it.sellingPrice === '' || it.sellingPrice == null)
        return `Line ${i + 1}: menu price is required.`;
      const selling = parseFloat(it.sellingPrice);
      if (!Number.isFinite(selling) || selling < 0)
        return `Line ${i + 1}: menu price is invalid.`;
      if (lineNeedsCategory(it))
        return `Line ${i + 1}: menu category is required for new products.`;
    }
    return null;
  };

  const handleSave = async () => {
    setError(null);
    const v = validate();
    if (v) return setError(v);
    try {
      await updateImport.mutateAsync({ uuid, data: buildPayload() });
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save changes');
    }
    return undefined;
  };

  const handleConfirm = async () => {
    setError(null);
    const v = validate();
    if (v) return setError(v);
    try {
      await updateImport.mutateAsync({ uuid, data: buildPayload() });
      const result = await confirmImport.mutateAsync({
        uuid,
        data: {
          businessUuid,
          vendorUuid: vendorUuid || undefined,
          warehouseUuid: warehouseUuid || undefined,
        },
      });
      if (result.invoiceUuid) {
        history.replace(`/inventory-management/purchase-invoices/${result.invoiceUuid}`);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to confirm invoice');
    }
    return undefined;
  };

  const handleDelete = async () => {
    try {
      await deleteImport.mutateAsync({ uuid, businessUuid });
      setDeleteOpen(false);
      history.push('/inventory-management/purchase-invoices');
    } catch (e) {
      setDeleteOpen(false);
      setError(e?.response?.data?.message || 'Failed to delete');
    }
  };

  const centered = (children) => (
    <Box sx={{ py: 10, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      {children}
    </Box>
  );

  if (isLoading || !invoice) {
    return (
      <>
        <PageHeader title="Invoice Import" backTo="/inventory-management/purchase-invoices" />
        {centered(<CircularProgress sx={{ color: colors.primary }} />)}
      </>
    );
  }

  if (invoice.status === 'processing') {
    return (
      <>
        <PageHeader title="Extracting Invoice" backTo="/inventory-management/purchase-invoices" />
        {centered(
          <>
            <CircularProgress sx={{ color: colors.primary }} />
            <Typography variant="h6" sx={{ color: colors.navy }}>Reading your invoice...</Typography>
            <Typography variant="body2" sx={{ color: colors.gray500 }}>
              AI is extracting line items and totals. This usually takes a few seconds.
            </Typography>
          </>
        )}
      </>
    );
  }

  if (invoice.status === 'failed') {
    return (
      <>
        <PageHeader title="Extraction Failed" backTo="/inventory-management/purchase-invoices" />
        {centered(
          <>
            <ErrorOutlineIcon sx={{ fontSize: 42, color: colors.error }} />
            <Typography variant="h6" sx={{ color: colors.navy }}>We couldn't read this invoice</Typography>
            <Typography variant="body2" sx={{ color: colors.gray500 }}>{invoice.error || 'Unknown error.'}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)} sx={{ textTransform: 'none' }}>
                Delete
              </Button>
              <Button
                variant="contained"
                onClick={() => retryImport.mutateAsync({ uuid, businessUuid })}
                disabled={retryImport.isPending}
                sx={{ backgroundColor: colors.primary, textTransform: 'none' }}
              >
                Retry
              </Button>
            </Stack>
          </>
        )}
        <ConfirmDialog
          open={deleteOpen}
          title="Discard Import"
          message="This will permanently delete the uploaded file and extracted data."
          confirmLabel="Delete"
          destructive
          loading={deleteImport.isPending}
          onConfirm={handleDelete}
          onClose={() => setDeleteOpen(false)}
        />
      </>
    );
  }

  if (invoice.status === 'confirmed') {
    return (
      <>
        <PageHeader title="Invoice Created" backTo="/inventory-management/purchase-invoices" />
        {centered(
          <>
            <CheckCircleIcon sx={{ fontSize: 48, color: colors.success }} />
            <Typography variant="h6" sx={{ color: colors.navy }}>Invoice created</Typography>
          </>
        )}
      </>
    );
  }

  const validation = invoice.extraction?.validation;
  const overallConfidence = invoice.extraction?.overallConfidence ?? null;

  return (
    <>
      <PageHeader title="Review Invoice" backTo="/inventory-management/purchase-invoices" />

      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        {/* Scanned pages */}
        {invoice.file?.pageCount > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <DetailCard title="Scanned Pages">
              <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                {Array.from({ length: invoice.file.pageCount }).map((_, idx) => (
                  <InvoiceImportPageImage key={idx} uuid={invoice.uuid} index={idx} />
                ))}
              </Stack>
            </DetailCard>
          </Box>
        )}

        {/* Confidence + validation */}
        <Box sx={{ mb: 2.5 }}>
          <DetailCard>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {overallConfidence != null && (
                <StatusChip
                  status={overallConfidence >= 0.8 ? 'success' : 'warning'}
                  label={`AI confidence ${Math.round(overallConfidence * 100)}%`}
                />
              )}
              <StatusChip
                status={validation?.totalsReconcile ? 'success' : 'warning'}
                label={validation?.totalsReconcile ? 'Totals match' : 'Totals mismatch'}
              />
              <StatusChip
                status={validation?.lineMath ? 'success' : 'warning'}
                label={validation?.lineMath ? 'Line math OK' : 'Line math off'}
              />
            </Stack>
            {validation?.warnings?.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                {validation.warnings.map((w, i) => (
                  <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
                    <ReportProblemOutlinedIcon sx={{ fontSize: 14, color: colors.warning, mt: '2px' }} />
                    <Typography variant="caption" sx={{ color: colors.gray600 }}>{w}</Typography>
                  </Stack>
                ))}
              </Box>
            )}
          </DetailCard>
        </Box>

        {/* Error banner */}
        {error && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2.5,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: '#fdecea',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 20, color: colors.error }} />
            <Typography variant="body2" sx={{ color: colors.error, fontWeight: 500 }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Invoice details */}
        <Box sx={{ mb: 2.5 }}>
          <DetailCard title="Invoice Details">
            <Stack spacing={2}>
              <TextField
                select
                label="Vendor"
                value={vendorUuid}
                onChange={(e) => setVendorUuid(e.target.value)}
                fullWidth
                size="small"
                helperText={invoice.extraction?.vendorName ? `Detected: ${invoice.extraction.vendorName}` : ' '}
              >
                <MenuItem value="">Select vendor</MenuItem>
                {vendorOptions.map((v) => (
                  <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                ))}
              </TextField>
              <TextField label="Vendor Invoice" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} fullWidth size="small" placeholder="Vendor invoice number" />
              <TextField label="Invoice Date" type="date" InputLabelProps={{ shrink: true }} value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} fullWidth size="small" />
              {SHOW_CURRENCY_FIELD && (
                <TextField label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} fullWidth size="small" placeholder="USD" />
              )}
              {SHOW_WAREHOUSE_FIELDS && (
                <TextField select label="Warehouse (optional)" value={warehouseUuid} onChange={(e) => setWarehouseUuid(e.target.value)} fullWidth size="small">
                  <MenuItem value="">Select warehouse</MenuItem>
                  {warehouseOptions.map((w) => (
                    <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                  ))}
                </TextField>
              )}
              <TextField label="Tax Amount" type="number" value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)} fullWidth size="small" placeholder="0.00" />
              <TextField label="Shipping Cost" type="number" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} fullWidth size="small" placeholder="0.00" />
            </Stack>
          </DetailCard>
        </Box>

        {/* Line items */}
        <Box sx={{ mb: 2.5 }}>
          <DetailCard
            title={`Line Items (${items.length})`}
            action={
              <IconButton size="small" onClick={addItem} sx={{ color: colors.primary }}>
                <AddCircleOutlineIcon />
              </IconButton>
            }
          >
            {suggestionCount > 0 && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CompareArrowsIcon />}
                onClick={linkAllSuggestions}
                sx={{ mb: 2, textTransform: 'none', color: colors.primary, borderColor: colors.primary }}
              >
                Link all {suggestionCount} suggested{' '}
                {suggestionCount === 1 ? 'match' : 'matches'}
              </Button>
            )}
            <Box sx={LINE_ITEMS_CONTAINER_SX}>
              {items.map((item) => (
                <InvoiceImportLineItemRow
                  key={item.id}
                  item={item}
                  categories={categories}
                  categoryById={categoryById}
                  showNoMatch={productMatchTried}
                  hasCategoryError={missingCategoryIds.has(item.id)}
                  onUpdate={updateItem}
                  onRemove={removeItem}
                  onItemSelected={handleItemSelected}
                  onBarcodeScan={handleBarcodeScanRequest}
                  onCategoryChange={handleCategoryChange}
                />
              ))}
            </Box>
          </DetailCard>
        </Box>

        {/* Totals */}
        <Box sx={{ mb: 2.5 }}>
          <DetailCard>
            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Typography variant="body2" sx={{ color: colors.gray600 }}>Subtotal</Typography>
              <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Typography variant="body2" sx={{ color: colors.gray600 }}>Tax</Typography>
              <Typography variant="body2">{formatCurrency(parseFloat(taxAmount) || 0)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Typography variant="body2" sx={{ color: colors.gray600 }}>Shipping</Typography>
              <Typography variant="body2">{formatCurrency(parseFloat(shippingCost) || 0)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, pt: 1.25, borderTop: `1px solid ${colors.border}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.navy }}>Total</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.primary }}>{formatCurrency(total)}</Typography>
            </Stack>
          </DetailCard>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={updateImport.isPending || confirmImport.isPending}
            sx={{ textTransform: 'none', flex: 1, py: 1.25 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={confirmImport.isPending}
            sx={{ backgroundColor: colors.primary, textTransform: 'none', flex: 1, py: 1.25 }}
          >
            Confirm &amp; Create
          </Button>
        </Stack>
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Discard Import
          </Button>
        </Box>
      </Box>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemFound={handleBarcodeFound}
        onBarcodeNotFound={handleBarcodeNotFound}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Discard Import"
        message="This will permanently delete the uploaded file and extracted data."
        confirmLabel="Delete"
        destructive
        loading={deleteImport.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}
